import omit from 'lodash/omit';

import type { CompositionV3Filters, EntKind } from '@revelio/data-access';

import { entKindToFilterKey } from '../role-skill-ent-kind';
import type {
  RoleLadder,
  TreemapDrilldownSegment,
} from '../treemapDrilldownLevels';
import {
  getBreakdownLevelAtDepth,
  getBreakdownLevelDepth,
} from './treemapNestedLevels';

const PARENT_NARROWABLE_SEGMENTS: readonly TreemapDrilldownSegment[] = [
  'geography',
  'role',
  'skills',
  'activities',
];

export function isParentNarrowableSegment(
  segment: TreemapDrilldownSegment
): boolean {
  return PARENT_NARROWABLE_SEGMENTS.includes(segment);
}

function getAncestorLevelFilterKeys({
  segment,
  parentEntKind,
  geographySelectionListIds,
  roleLadder,
}: {
  segment: TreemapDrilldownSegment;
  parentEntKind: EntKind;
  geographySelectionListIds: readonly string[];
  roleLadder: RoleLadder;
}): (keyof CompositionV3Filters)[] {
  const parentDepth = getBreakdownLevelDepth(
    segment,
    parentEntKind,
    geographySelectionListIds,
    roleLadder
  );
  if (parentDepth === null || parentDepth <= 0) {
    return [];
  }
  return Array.from({ length: parentDepth }, (_, depth) =>
    getBreakdownLevelAtDepth(
      segment,
      depth,
      geographySelectionListIds,
      roleLadder
    )
  )
    .map((level) => (level === null ? null : entKindToFilterKey(level)))
    .filter((key): key is keyof CompositionV3Filters => key !== null);
}

export function narrowCompositionV3FiltersToParents({
  filters,
  segment,
  parentEntKind,
  parentIds,
  geographySelectionListIds,
  roleLadder = 'standard',
}: {
  filters: CompositionV3Filters;
  segment: TreemapDrilldownSegment;
  parentEntKind: EntKind | null;
  parentIds: readonly string[];
  geographySelectionListIds: readonly string[];
  roleLadder?: RoleLadder;
}): CompositionV3Filters {
  if (parentEntKind === null || parentIds.length === 0) {
    return filters;
  }

  const key = entKindToFilterKey(parentEntKind);
  if (key === null) {
    return filters;
  }

  const numericIds = parentIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
  if (numericIds.length !== parentIds.length) {
    return filters;
  }

  const parentLevelIds = ((): number[] | null => {
    const existing = filters[key];
    if (!Array.isArray(existing) || existing.length === 0) {
      return numericIds;
    }
    const rendered = new Set<number>(numericIds);
    const intersection = existing.filter(
      (id): id is number => typeof id === 'number' && rendered.has(id)
    );
    if (intersection.length === 0 || intersection.length === existing.length) {
      return null;
    }
    return intersection;
  })();
  if (parentLevelIds === null) {
    return filters;
  }

  return omit(
    { ...filters, [key]: parentLevelIds },
    getAncestorLevelFilterKeys({
      segment,
      parentEntKind,
      geographySelectionListIds,
      roleLadder,
    })
  );
}
